import * as React from 'react';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';
import { Backdrop, Grow, Popper } from '@mui/material';
import { Paper, Stack } from '@mui/material';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import {
  useGetCubeAttributesDropdown,
  useGetLayerListDropdownByCube,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';

// ─── Styled Popper with arrow ────────────────────────────────────────────────

const StyledPopper = styled(Popper)(() => ({
  zIndex: 1000,
  '&[data-popper-placement*="bottom"] .popper-arrow': {
    top: 0,
    marginTop: '-0.71em',
    '&::before': {
      transformOrigin: '0 100%',
    },
  },
  '&[data-popper-placement*="top"] .popper-arrow': {
    bottom: 0,
    marginBottom: '-0.71em',
    '&::before': {
      transformOrigin: '100% 0',
    },
  },
  '&[data-popper-placement*="right"] .popper-arrow': {
    left: 0,
    marginLeft: '-0.71em',
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '100% 100%',
    },
  },
  '&[data-popper-placement*="left"] .popper-arrow': {
    right: 0,
    marginRight: '-0.71em',
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '0 0',
    },
  },
}));

const PopperArrow = styled('span')(() => ({
  overflow: 'hidden',
  position: 'absolute',
  width: '1em',
  height: '0.71em',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    transform: 'rotate(45deg)',
    boxShadow: '0px 0px 4px rgba(0,0,0,0.15)',
  },
}));

// ─── Types ────────────────────────────────────────────────────────────────────

export const CUBE_CHART_TYPE_OPTIONS = [
  {
    value: 'LINE_CHART',
    label: 'Line Chart',
    icon: <StackedLineChartOutlinedIcon sx={{ fontSize: 16, color: '#42BB93' }} />,
  },
  {
    value: 'VERTICAL_BAR_CHART',
    label: 'Bar Chart',
    icon: <BarChartOutlinedIcon sx={{ fontSize: 16, color: '#42BB93' }} />,
  },
];

export interface FormCubeChartDisplayParameter {
  attribute: AutoCompleteItem[];
  layer: DropdownItem[];
  chartType: string;
}

interface ButtonCubeChartDisplayProps {
  onDisplayChart?: (data: FormCubeChartDisplayParameter) => void;
  disabled?: boolean;
  cubeId?: string;
  memberId?: string;
  initialValue?: {
    layer: DropdownItem | null;
  };
}

// ─── Validation ───────────────────────────────────────────────────────────────

const chartDisplayValidationSchema = Yup.object().shape({
  attribute: Yup.array().min(1, 'Required'),
  layer: Yup.array().min(1, 'Required'),
  chartType: Yup.string().required('Required'),
});

const formikInitialValues: FormCubeChartDisplayParameter = {
  attribute: [],
  layer: [],
  chartType: 'LINE_CHART',
};

// ─── Autocomplete styles (matching sibling components) ────────────────────────

const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ButtonCubeChartDisplay(props: ButtonCubeChartDisplayProps) {
  const { onDisplayChart, disabled, cubeId, memberId, initialValue } = props;

  const { data: cubeAttributesDropdown } = useGetCubeAttributesDropdown(cubeId, memberId);
  const { data: layerDropdown } = useGetLayerListDropdownByCube(cubeId);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formik = useFormik<FormCubeChartDisplayParameter>({
    initialValues: formikInitialValues,
    validationSchema: chartDisplayValidationSchema,
    onSubmit: (values, formikHelper) => {
      handlePopoverClose();
      onDisplayChart && onDisplayChart(values);
      formikHelper.resetForm();
    },
  });

  // Pre-fill layer from initialValue when popper opens
  React.useEffect(() => {
    if (initialValue?.layer && open) {
      formik.setFieldValue('layer', [initialValue.layer]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, open]);

  const layerOptions: DropdownItem[] = React.useMemo(() => {
    return layerDropdown?.data ?? [];
  }, [layerDropdown?.data]);

  const attributeOptions: AutoCompleteItem[] = React.useMemo(() => {
    return cubeAttributesDropdown?.data ?? [];
  }, [cubeAttributesDropdown?.data]);

  return (
    <div>
      <Backdrop open={open} sx={{ zIndex: 999 }} />

      <Tooltip title='Display Chart'>
        <span data-testid='button-chart-display-label'>
          <IconButton data-testid='button-chart-display' onClick={handlePopoverOpen} disabled={disabled}>
            <BarChartOutlinedIcon sx={{ color: disabled ? '#ccc' : '#42BB93' }} />
          </IconButton>
        </span>
      </Tooltip>

      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement='left-start'
        disablePortal={false}
        transition
        modifiers={[
          { name: 'flip', options: { altBoundary: true, rootBoundary: 'document', padding: 8 } },
          { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 8 } },
          { name: 'arrow', options: { element: arrowRef } },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={250}>
            <div>
              <PopperArrow className='popper-arrow' ref={setArrowRef} />

              <Paper
                elevation={8}
                sx={{
                  width: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                {/* Header */}
                <Stack sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                    <Stack>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#3B4797', lineHeight: 1.3 }}>
                        Chart Display
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#98A2AE', mt: 0.25 }}>
                        Select attributes and layers to visualize
                      </Typography>
                    </Stack>
                    <IconButton size='small' onClick={handlePopoverClose} sx={{ mt: -0.5 }}>
                      <HighlightOffRoundedIcon sx={{ color: '#98A2AE', fontSize: 20 }} />
                    </IconButton>
                  </Stack>
                </Stack>
                <Divider />

                {/* Body */}
                <Stack sx={{ px: 2, py: 1.5 }}>
                  {/* Attribute */}
                  <Stack mb={1.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Attribute</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Choose which data points to plot
                    </Typography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size='small'
                      options={attributeOptions}
                      getOptionLabel={(option: AutoCompleteItem) => (option.label as string) || ''}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={formik.values.attribute}
                      sx={autocompleteStyles}
                      onChange={(_event, newValue) => {
                        formik.setFieldValue('attribute', newValue);
                      }}
                      renderTags={(value) =>
                        value.length > 0 ? (
                          <Chip
                            label={value.length === 1 ? (value[0].label as string) : `${value.length} attributes selected`}
                            size='small'
                            onDelete={() => formik.setFieldValue('attribute', [])}
                            sx={{ fontSize: '11px', height: 24, bgcolor: '#EDF2FF', color: '#3B4797', fontWeight: 600 }}
                          />
                        ) : null
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ ...props.style, padding: '2px 8px' }}>
                          <Checkbox
                            checked={selected}
                            size='small'
                            sx={{ mr: 0.5, p: 0.5, color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                          />
                          <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
                        </li>
                      )}
                      clearIcon={null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Attribute...'
                          error={Boolean(formik.touched.attribute && formik.errors.attribute)}
                          helperText={
                            formik.touched.attribute && formik.values.attribute.length === 0
                              ? 'At least 1 attribute required'
                              : undefined
                          }
                        />
                      )}
                    />
                  </Stack>

                  {/* Layer */}
                  <Stack mb={1.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Layer</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Select data layers to compare
                    </Typography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size='small'
                      options={layerOptions}
                      value={formik.values.layer}
                      sx={autocompleteStyles}
                      getOptionLabel={(option: DropdownItem) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_event, newValue) => {
                        formik.setFieldValue('layer', newValue);
                      }}
                      renderTags={(value) =>
                        value.length > 0 ? (
                          <Chip
                            label={value.length === 1 ? value[0].label : `${value.length} layers selected`}
                            size='small'
                            onDelete={() => formik.setFieldValue('layer', [])}
                            sx={{ fontSize: '11px', height: 24, bgcolor: '#EDF2FF', color: '#3B4797', fontWeight: 600 }}
                          />
                        ) : null
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ ...props.style, padding: '2px 8px' }}>
                          <Checkbox
                            checked={selected}
                            size='small'
                            sx={{ mr: 0.5, p: 0.25, color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                          />
                          <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
                        </li>
                      )}
                      clearIcon={null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Layer...'
                          error={Boolean(formik.touched.layer && formik.errors.layer)}
                          helperText={
                            formik.touched.layer && formik.values.layer.length === 0
                              ? 'At least 1 layer required'
                              : undefined
                          }
                        />
                      )}
                    />
                  </Stack>

                  {/* Chart Type */}
                  <Stack mb={2}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 0.25 }}>Chart Type</Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#98A2AE', mb: 0.75 }}>
                      Pick how the data is displayed
                    </Typography>
                    <Stack direction='row' gap={1}>
                      {CUBE_CHART_TYPE_OPTIONS.map((opt) => {
                        const isSelected = formik.values.chartType === opt.value;
                        return (
                          <Stack
                            key={opt.value}
                            direction='row'
                            alignItems='center'
                            gap={0.75}
                            onClick={() => formik.setFieldValue('chartType', opt.value)}
                            sx={{
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 1.5,
                              border: isSelected ? '1.5px solid #42BB93' : '1px solid #e0e3e8',
                              bgcolor: isSelected ? '#f0faf6' : '#fff',
                              cursor: 'pointer',
                              userSelect: 'none',
                              transition: 'all 0.15s ease',
                              '&:hover': { borderColor: '#42BB93', bgcolor: '#f0faf6' },
                            }}
                          >
                            {opt.icon}
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: isSelected ? '#2a7d63' : '#555',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Stack>

                  {/* Footer actions */}
                  <Stack direction='row' spacing={1.5} mt={3} justifyContent='flex-end'>
                    <Button size='small' onClick={handlePopoverClose} variant='main-table-panel-border'>
                      Cancel
                    </Button>
                    <ButtonLoading
                      size='small'
                      variant='main-table-panel'
                      onClick={() => formik.handleSubmit()}
                      disabled={!chartDisplayValidationSchema.isValidSync(formik.values) || disabled}
                    >
                      Display Chart
                    </ButtonLoading>
                  </Stack>
                </Stack>
              </Paper>
            </div>
          </Grow>
        )}
      </StyledPopper>
    </div>
  );
}
